// AdminInfraStatusBanner
//
// Admin-only top-of-app status strip that surfaces infra problems
// the operator can't see from any single feature page:
//   - Twilio parent account balance ≤ low/critical threshold (BUG-013)
//   - A2P 10DLC parent brand in FAILED state (BUG-002)
//
// Mounts inside the main app shell when currentUser.role === 'admin'.
// Polls each endpoint once on mount + every 5 min while open. Dismiss
// hides the banner for the current tab session only — refresh restores
// it so a long-lived issue resurfaces.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { AlertTriangle, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

const POLL_MS = 5 * 60 * 1000;

async function apiFetch(path) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    headers: { ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) throw new Error((body && body.error) || `HTTP ${res.status}`);
  return body;
}

export default function AdminInfraStatusBanner({ currentUser }) {
  const [balance, setBalance] = React.useState(null);
  const [a2p, setA2p]         = React.useState(null);
  const [dismissed, setDismissed] = React.useState(false);

  React.useEffect(() => {
    if (currentUser?.role !== 'admin') return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const b = await apiFetch('/api/admin/twilio-balance');
        if (!cancelled) setBalance(b);
      } catch { /* not configured or non-admin → hide silently */ }
      try {
        const a = await apiFetch('/api/twilio/a2p-status');
        if (!cancelled) setA2p(a);
      } catch { /* hide silently */ }
    };
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [currentUser?.id, currentUser?.role]);

  if (currentUser?.role !== 'admin') return null;
  if (dismissed) return null;

  // Compose the issues list. Each issue is one row in the banner so
  // the admin sees ALL infra problems at once, not just the first.
  const issues = [];
  if (balance?.low_balance) {
    issues.push({
      key: 'twilio_balance',
      severity: balance.tier === 'critical' ? 'critical' : 'warn',
      // Disambiguates from the in-app AI Wallet shown in Settings.
      // Operators were reading "Twilio balance $20" + "AI Wallet
      // $96.86" as the same number disagreeing with itself. They're
      // two different ledgers — Twilio's upstream account vs the
      // per-tenant CRM credit wallet that pays for AI / SMS / voice.
      label: `Relay platform balance ${balance.balance_usd != null ? `$${balance.balance_usd.toFixed(2)}` : '(unknown)'} (separate from your CRM AI Wallet)`,
      detail: balance.ops_note || `Below the low threshold ($${balance.threshold_usd}). This is the PLATFORM Relay billing balance — it's what tops up the per-tenant wallets. Top up before onboarding more tenants.`,
      cta: { label: 'Open carrier console', href: 'https://console.twilio.com/us1/billing/manage-billing/billing-overview' },
    });
  }
  // A2P parent brand FAILED is the canonical block on SMS dispatch
  // for the whole platform. parentBrand is the field exposed by
  // /api/twilio/a2p-status; the shape evolved over time, so guard
  // each lookup.
  const brandStatus = String(
    a2p?.parentBrand?.status
    || a2p?.brand?.status
    || a2p?.status
    || ''
  ).toUpperCase();
  if (brandStatus === 'FAILED') {
    issues.push({
      key: 'a2p_failed',
      severity: 'critical',
      label: 'A2P parent brand: FAILED',
      detail: 'No SMS will dispatch until the brand passes vetting. Review the rejection reason + resubmit in the carrier console.',
      cta: { label: 'Open A2P Console', href: 'https://console.twilio.com/us1/develop/sms/regulatory-compliance/a2p-10dlc' },
    });
  } else if (brandStatus === 'IN_REVIEW' || brandStatus === 'PENDING') {
    issues.push({
      key: 'a2p_pending',
      severity: 'warn',
      label: `A2P brand status: ${brandStatus.replace('_', ' ')}`,
      detail: 'Brand registration is in flight. SMS may be limited until approved.',
      cta: { label: 'Open A2P Console', href: 'https://console.twilio.com/us1/develop/sms/regulatory-compliance/a2p-10dlc' },
    });
  }

  if (issues.length === 0) return null;

  const headline = issues.some(i => i.severity === 'critical')
    ? 'Critical infra issue — admin action required'
    : 'Infra status — review';
  const bg = issues.some(i => i.severity === 'critical')
    ? 'bg-gradient-to-r from-rose-700 to-rose-800'
    : 'bg-gradient-to-r from-amber-600 to-amber-700';

  return (
    <div className={`relative z-30 ${bg} text-white px-4 py-2 shadow-md`}>
      <div className="max-w-none mx-auto flex items-start gap-3">
        <AlertTriangle size={16} className="shrink-0 mt-0.5"/>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] opacity-90">{headline}</div>
          <ul className="mt-0.5 space-y-0.5">
            {issues.map(i => (
              <li key={i.key} className="text-sm leading-snug">
                <strong className="font-semibold">{i.label}.</strong>{' '}
                <span className="opacity-95">{i.detail}</span>
                {i.cta && (
                  <a
                    href={i.cta.href}
                    target="_blank" rel="noopener noreferrer"
                    className="ml-2 underline font-semibold whitespace-nowrap hover:no-underline"
                  >
                    {i.cta.label} ↗
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss for this session"
          title="Dismiss for this tab session — refresh restores it"
          className="shrink-0 text-white/80 hover:text-white text-xl leading-none px-2 -mt-1"
        >
          <X size={16}/>
        </button>
      </div>
    </div>
  );
}
